import { format, subDays } from 'date-fns';

export type ListenerType = {
  type: 'Explorer' | 'Devotee' | 'Variety' | 'Focused' | 'Casual';
  description: string;
  traits: string[];
};

interface ListenerMetrics {
  skipRate: number;
  uniqueArtistsRatio: number;
  repeatPlayRate: number;
  dailyListeningTime: number;
  genreDiversity: number;
}

export const calculateListenerType = (metrics: ListenerMetrics): ListenerType => {
  const types: Record<ListenerType['type'], ListenerType> = {
    Explorer: {
      type: 'Explorer',
      description: 'Always seeking new music and artists',
      traits: [
        'High skip rate',
        'Many unique artists',
        'Short listening sessions',
        'Diverse genres',
        'Frequently adds new tracks'
      ]
    },
    Devotee: {
      type: 'Devotee',
      description: 'Deep appreciation for favorite artists',
      traits: [
        'Low skip rate',
        'Long listening sessions',
        'High repeat plays',
        'Focused genre preferences',
        'Creates detailed playlists'
      ]
    },
    Variety: {
      type: 'Variety',
      description: 'Balanced and eclectic taste',
      traits: [
        'Moderate skip rate',
        'Mixed artist rotation',
        'Regular listening patterns',
        'Wide genre range',
        'Organized collections'
      ]
    },
    Focused: {
      type: 'Focused',
      description: 'Intentional and methodical listener',
      traits: [
        'Very low skip rate',
        'Complete album plays',
        'Long sessions',
        'Genre specialist',
        'Curated playlists'
      ]
    },
    Casual: {
      type: 'Casual',
      description: 'Relaxed and spontaneous approach',
      traits: [
        'Variable skip rate',
        'Shorter sessions',
        'Mood-based selection',
        'Popular tracks focus',
        'Background listening'
      ]
    }
  };

  // Calculate scores for each type based on metrics
  const scores = {
    Explorer: (
      metrics.skipRate * 0.3 +
      metrics.uniqueArtistsRatio * 0.3 +
      metrics.genreDiversity * 0.2 +
      (1 - metrics.repeatPlayRate) * 0.2
    ),
    Devotee: (
      (1 - metrics.skipRate) * 0.3 +
      metrics.repeatPlayRate * 0.3 +
      (metrics.dailyListeningTime / 4) * 0.2 +
      (1 - metrics.genreDiversity) * 0.2
    ),
    Variety: (
      (1 - Math.abs(0.5 - metrics.skipRate)) * 0.25 +
      metrics.genreDiversity * 0.25 +
      metrics.uniqueArtistsRatio * 0.25 +
      (metrics.dailyListeningTime / 3) * 0.25
    ),
    Focused: (
      (1 - metrics.skipRate) * 0.35 +
      metrics.repeatPlayRate * 0.25 +
      (metrics.dailyListeningTime / 4) * 0.2 +
      (1 - metrics.genreDiversity) * 0.2
    ),
    Casual: (
      (1 - metrics.dailyListeningTime / 4) * 0.3 +
      (1 - metrics.repeatPlayRate) * 0.3 +
      (1 - Math.abs(0.5 - metrics.skipRate)) * 0.2 +
      (1 - metrics.uniqueArtistsRatio) * 0.2
    )
  };

  // Find the type with the highest score
  const listenerType = Object.entries(scores)
    .reduce((max, [type, score]) => 
      score > max.score ? { type, score } : max,
      { type: 'Casual', score: -1 }
    ).type as ListenerType['type'];

  return types[listenerType];
};